<template>
  <div 
    v-if="slidesStore.showBeforeAfterOverlay" 
    class="before-after-overlay"
    @click="handleOverlayClick"
  >
    <!-- Image comparison component - fullscreen -->
    <ImageComparison
      :before-image="beforeImagePath"
      :after-image="afterImagePath"
      :before-alt="`Old ${currentSlideName} design`"
      :after-alt="`New ${currentSlideName} design`"
      :full-slide="true"
      :initial-value="50"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue'
import { useSlidesStore } from '~/stores/slides'
import ImageComparison from './ImageComparison.vue'

const slidesStore = useSlidesStore()

// Computed properties for current slide info
const currentSlide = computed(() => slidesStore.currentSlide)
const currentSlideName = computed(() => currentSlide.value?.name || 'Slide')
const slideNumber = computed(() => `${slidesStore.currentSlideIndex + 1}/${slidesStore.totalSlides}`)

// Generate image paths based on current slide
const beforeImagePath = computed(() => {
  // Use a transparent/empty image so the live slide shows through as "before"
  // This creates the effect of the live slide being the starting point
  return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMSIgaGVpZ2h0PSIxIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InRyYW5zcGFyZW50Ii8+PC9zdmc+'
})

const afterImagePath = computed(() => {
  // The old slide images (1.jpg, 2.jpg, etc.) are revealed when dragging the slider
  const slideIndex = slidesStore.currentSlideIndex + 1
  return `/${slideIndex}.jpg`
})

// Handle clicking outside the controls to close overlay
const handleOverlayClick = (event: MouseEvent) => {
  // Only close if clicking directly on the overlay background
  if (event.target === event.currentTarget) {
    slidesStore.toggleBeforeAfterOverlay()
  }
}

// Handle image loading errors
const handleImageError = (event: Event) => {
  console.warn('Failed to load image:', beforeImagePath.value)
  // You could show a placeholder or error message here
}

// Handle keyboard navigation
const handleKeydown = (event: KeyboardEvent) => {
  if (!slidesStore.showBeforeAfterOverlay) return
  
  switch (event.key) {
    case 'Escape':
      event.preventDefault()
      slidesStore.toggleBeforeAfterOverlay()
      break
    case 'ArrowLeft':
      event.preventDefault()
      slidesStore.previousSlide()
      break
    case 'ArrowRight':
      event.preventDefault()
      slidesStore.nextSlide()
      break
  }
}

// Add keyboard listener when component mounts
onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
.before-after-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 40;
  background: transparent;
  cursor: pointer;
}

.before-image-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-width: 90vw;
  max-height: 80vh;
  z-index: 1000;
}

.before-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  border: 2px solid rgba(255, 255, 255, 0.1);
}

.overlay-controls {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1001;
  pointer-events: none;
}

.control-panel {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  pointer-events: auto;
}

.slide-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: white;
}

.slide-number {
  font-size: 0.875rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.2);
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  backdrop-filter: blur(10px);
}

.slide-name {
  font-size: 1rem;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

.control-buttons {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.control-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  backdrop-filter: blur(10px);
  transition: all 0.2s ease;
}

.control-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
}

.control-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.close-btn:hover {
  background: rgba(239, 68, 68, 0.2);
  border-color: rgba(239, 68, 68, 0.3);
}

.instructions {
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  pointer-events: auto;
}

.instruction-text {
  color: white;
  font-size: 0.875rem;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  padding: 0.75rem 1.5rem;
  border-radius: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .control-panel {
    padding: 0.75rem 1rem;
    flex-direction: column;
    gap: 0.75rem;
    align-items: stretch;
  }
  
  .slide-info {
    justify-content: center;
  }
  
  .control-buttons {
    justify-content: center;
  }
  
  .instructions {
    bottom: 1rem;
    left: 1rem;
    right: 1rem;
    transform: none;
  }
  
  .instruction-text {
    font-size: 0.75rem;
    padding: 0.5rem 1rem;
    text-align: center;
    white-space: normal;
    line-height: 1.4;
  }
}

@media (max-width: 480px) {
  .control-panel {
    padding: 0.5rem;
  }
  
  .slide-name {
    font-size: 0.875rem;
  }
  
  .control-btn {
    padding: 0.5rem;
  }
}

/* Animation for overlay entrance */
.before-after-overlay {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    backdrop-filter: blur(0px);
  }
  to {
    opacity: 1;
    backdrop-filter: blur(2px);
  }
}
</style>
